import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Tag } from 'lucide-react';
import { postsAPI } from '../services/api';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const BlogIndex = () => {
  const [posts, setPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, [searchQuery, selectedTag]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const data = await postsAPI.getAll(searchQuery, selectedTag || '');
      setPosts(data);
      
      // Extract unique tags
      const tags = [...new Set(data.flatMap(post => post.tags))];
      setAllTags(tags);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-16">
          <h1 className="text-5xl font-semibold text-gray-900 mb-4 tracking-tight">
            Writing
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">
            Thoughts, reflections, and updates from Moss Group.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="mb-12 space-y-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 py-6 text-base border-gray-300 focus:border-gray-400"
            />
          </div>

          {allTags.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap">
              <Tag className="w-4 h-4 text-gray-500" />
              <button
                onClick={() => setSelectedTag(null)}
                className={`text-sm px-3 py-1 rounded-full border ${
                  !selectedTag 
                    ? 'bg-gray-900 text-white border-gray-900' 
                    : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                }`}
              >
                All
              </button>
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag)}
                  className={`text-sm px-3 py-1 rounded-full border ${
                    selectedTag === tag 
                      ? 'bg-gray-900 text-white border-gray-900' 
                      : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Posts List */}
        <div className="space-y-12">
          {loading ? (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">Loading articles...</p>
            </div>
          ) : posts.length > 0 ? (
            posts.map(post => (
              <article key={post.id} className="group">
                <Link to={`/post/${post.slug}`} className="block">
                  <div className="space-y-3">
                    <h2 className="text-3xl font-semibold text-gray-900 group-hover:text-gray-600 tracking-tight">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 text-lg leading-relaxed">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{post.author}</span>
                      <span>•</span>
                      <time dateTime={post.publishedAt}>
                        {new Date(post.publishedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </time>
                      <span>•</span>
                      <span>{post.readingTime} min read</span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {post.tags.map(tag => (
                        <Badge key={tag} variant="secondary" className="bg-gray-100 text-gray-700 hover:bg-gray-200">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Link>
              </article>
            ))
          ) : (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No articles found matching your search.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogIndex;
